import * as yup from 'yup';
import { type FieldArrayRenderProps, getIn, useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type DefaultName,
  type PhoneNumberData,
  type PhoneNumbersFormValues,
} from './FormikMultiPhoneNumbers.types';

import { DEFAULT_NAME, DEFAULT_PHONE_NUMBER_DATA, TYPES } from './FormikMultiPhoneNumbers.data';
import { detectPhoneNumberType } from '@/shared/utils/phoneUtils/phoneUtils';
import listUtils from '@/shared/utils/list';
import { removeWhitespaces } from '@/shared/utils/string';

import {
  createPhoneNumberLine,
  fieldPredicates,
  isPhoneNumberLine,
} from './FormikMultiPhoneNumbers.utils';

type FieldName = keyof Omit<PhoneNumberData, 'id'>;

type FieldNameParts<T extends FieldName = FieldName> = {
  index: number;
  name: T;
};

type InputTypes<T extends string> = {
  line: {
    fieldArrayName: T;
    index: number;
  };
  lines: {
    name: T;
  };
  schema: {
    name: T;
  };
};

const phoneNumberTypeValues = Object.values(TYPES);

const extractErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return undefined;

  const record = error as { [key: string]: unknown };

  if (typeof record[''] === 'string') return record[''];

  const stringError = Object.values(record).find((value) => typeof value === 'string');

  return typeof stringError === 'string' ? stringError : undefined;
};

const usePhoneNumberFormUtils = <T extends string>(input: InputTypes<T>['lines']) => {
  const formik = useFormikContext<PhoneNumbersFormValues<T>>();

  const hasSubmitted = !!formik.submitCount;

  const setFieldTouchedInternal = formik.setFieldTouched;
  const setFieldValueInternal = formik.setFieldValue;

  const composeName = useCallback(
    (parts: FieldNameParts) => `${input.name}[${parts.index}].${parts.name}`,
    [input.name],
  );

  const getTouched = useCallback((name: string) => getIn(formik.touched, name), [formik.touched]);

  const getError = useCallback(
    (name: string) => (getTouched(name) ? getIn(formik.errors, name) : undefined),
    [formik.errors, getTouched],
  );

  const getValue = useCallback((name: string) => getIn(formik.values, name), [formik.values]);

  const getFieldError = useCallback(
    (field: FieldNameParts) => {
      if (!hasSubmitted) return undefined;

      const fieldName = composeName(field);
      const error = getError(fieldName);

      return extractErrorMessage(error);
    },
    [composeName, getError, hasSubmitted],
  );

  const getGlobalError = useCallback(() => {
    if (!hasSubmitted) return undefined;

    const error = getError(input.name);

    return typeof error === 'string' ? error : undefined;
  }, [getError, hasSubmitted, input.name]);

  const getLineError = useCallback(
    (index: number) => {
      const globalError = getGlobalError();

      if (typeof globalError === 'string') return undefined;

      const lineName = `${input.name}[${index}]`;
      const lineError = getError(lineName);
      const lineTouched = getTouched(lineName);

      const isTouched = Object.values(lineTouched ?? {}).some(Boolean);
      const message = extractErrorMessage(lineError);

      return message && isTouched ? message : undefined;
    },
    [getError, getGlobalError, getTouched, input.name],
  );

  const getFieldValue = useCallback(
    <TField extends FieldName>(field: FieldNameParts<TField>) => {
      const fieldName = composeName(field);
      const formikValue = getValue(fieldName);

      return fieldPredicates[field.name](formikValue)
        ? formikValue
        : DEFAULT_PHONE_NUMBER_DATA[field.name];
    },
    [composeName, getValue],
  );

  const getValues = useCallback(() => {
    const values = getValue(input.name);

    if (!Array.isArray(values)) return [];

    return values.filter(isPhoneNumberLine);
  }, [getValue, input.name]);

  const setFieldTouched = useCallback(
    (field: FieldNameParts) => {
      const fieldName = composeName(field);

      setFieldTouchedInternal(fieldName, true);
    },
    [composeName, setFieldTouchedInternal],
  );

  const setFieldValue = useCallback(
    <TField extends FieldName>(field: FieldNameParts<TField>, value: PhoneNumberData[TField]) => {
      const fieldName = composeName(field);

      return setFieldValueInternal(fieldName, value);
    },
    [composeName, setFieldValueInternal],
  );

  const setValues = useCallback(
    (values: PhoneNumbersFormValues<T>[T]) => setFieldValueInternal(input.name, values),
    [input.name, setFieldValueInternal],
  );

  return useMemo(
    () => ({
      composeName,
      getFieldError,
      getFieldValue,
      getGlobalError,
      getLineError,
      getValues,
      setFieldTouched,
      setFieldValue,
      setValues,
    }),
    [
      composeName,
      getFieldError,
      getFieldValue,
      getGlobalError,
      getLineError,
      getValues,
      setFieldTouched,
      setFieldValue,
      setValues,
    ],
  );
};

const usePhoneNumberLine = <T extends string>(input: InputTypes<T>['line']) => {
  const {
    composeName,
    getFieldError,
    getFieldValue,
    getLineError,
    getValues,
    setFieldTouched,
    setFieldValue,
    setValues,
  } = usePhoneNumberFormUtils({
    name: input.fieldArrayName,
  });

  const getFieldProps = useCallback(
    <TField extends FieldName>(field: TField) => {
      const nameParts = { index: input.index, name: field };

      return {
        control: {
          error: getFieldError(nameParts),
        },
        input: {
          name: composeName(nameParts),
          onBlur: () => setFieldTouched(nameParts),
          onChange: (newValue: PhoneNumberData[TField]) => setFieldValue(nameParts, newValue),
          value: getFieldValue(nameParts),
        },
      };
    },
    [composeName, getFieldError, getFieldValue, input.index, setFieldTouched, setFieldValue],
  );

  return useMemo(() => {
    const clearDefaultWhenTypeAlreadyHasOne = (lineType: PhoneNumberData['type']) => {
      const values = getValues();
      const isDefaultLine = values[input.index]?.isDefault;
      const hasOtherDefaultOfType = values.some(
        (line, index) => index !== input.index && line.isDefault && line.type === lineType,
      );

      if (isDefaultLine && hasOtherDefaultOfType)
        setFieldValue({ index: input.index, name: 'isDefault' }, false);
    };

    return {
      countryCode: getFieldProps('countryCode'),
      isDefault: (() => {
        const field = getFieldProps('isDefault');

        return {
          ...field,
          input: {
            ...field.input,
            onChange: (isDefault: boolean) => {
              const values = getValues();
              const lineType = values[input.index]?.type;

              let nextLines = values.map((line) =>
                line.type === lineType ? { ...line, isDefault: false } : line,
              );

              if (isDefault)
                nextLines = listUtils.replace(
                  nextLines,
                  (line) => ({
                    ...line,
                    isDefault,
                  }),
                  input.index,
                );

              setValues(nextLines);
            },
          },
        };
      })(),
      number: getFieldProps('number'),
      get phoneNumber() {
        const handleBlur = () => {
          this.countryCode.input.onBlur();
          this.number.input.onBlur();

          clearDefaultWhenTypeAlreadyHasOne(this.type.input.value);
        };

        const handleChange = (number: string, countryCode: string) => {
          this.countryCode.input.onChange(countryCode);
          this.number.input.onChange(number);

          const detectedType = detectPhoneNumberType(number, Number(countryCode));

          if (detectedType && detectedType !== this.type.input.value)
            getFieldProps('type').input.onChange(detectedType);
        };

        return {
          control: {
            error:
              getLineError(input.index) ||
              this.countryCode.control.error ||
              this.number.control.error,
          },
          input: {
            onBlur: handleBlur,
            onChange: handleChange,
            value: this.countryCode.input.value + this.number.input.value,
          },
        };
      },
      remark: getFieldProps('remark'),
      type: (() => {
        const field = getFieldProps('type');

        return {
          ...field,
          input: {
            ...field.input,
            onChange: (newType: PhoneNumberData['type']) => {
              field.input.onChange(newType);
              clearDefaultWhenTypeAlreadyHasOne(newType);
            },
          },
        };
      })(),
    };
  }, [getFieldProps, getLineError, getValues, input.index, setFieldValue, setValues]);
};

const usePhoneNumberLines = <T extends string>(input: InputTypes<T>['lines']) => {
  const { getGlobalError, getValues } = usePhoneNumberFormUtils({ name: input.name });

  const lines = useMemo(() => getValues(), [getValues]);

  const push = useCallback(
    (fieldArray: FieldArrayRenderProps) => {
      const hasDefaultLine = lines.some(
        (line) => line.isDefault && line.type === DEFAULT_PHONE_NUMBER_DATA.type,
      );

      const emptyLine = createPhoneNumberLine(undefined, !hasDefaultLine);

      fieldArray.push(emptyLine);
    },
    [lines],
  );

  const remove = useCallback(
    (fieldArray: FieldArrayRenderProps, index: number) => fieldArray.remove(index),
    [],
  );

  const helpers = useMemo(
    () => ({
      getGlobalError,
      push,
      remove,
    }),
    [getGlobalError, push, remove],
  );

  return [lines, helpers] as const;
};

const usePhoneNumbersSchema = <T extends string = DefaultName>(input?: InputTypes<T>['schema']) => {
  const { t } = useTranslation('errors');

  const multiPhoneNumberSchema = useMemo(
    () =>
      yup
        .object({
          countryCode: yup.string().required(t('fieldIsRequired')),
          isDefault: yup.boolean(),
          number: yup.string().required(t('fieldIsRequired')),
          remark: yup.string().nullable(),
          type: yup.string().oneOf(phoneNumberTypeValues),
        })
        .when('.type', {
          is: (type: string) => type === TYPES.LANDLINE,
          otherwise: (schema) => schema.phoneNumber(true),
          then: (schema) => schema.phoneNumber(false),
        }),
    [t],
  );

  const name = input?.name ?? DEFAULT_NAME;

  const schema = useMemo(
    () =>
      yup.object({
        [name]: yup
          .array(multiPhoneNumberSchema)
          .test('hasDefault', t('defaultValueIsRequired'), (list) => {
            if (!list?.length) return true;

            return list.some((phoneNumber) => phoneNumber.isDefault);
          })
          .test('hasOneDefaultPerType', t('onlyOneDefaultPerPhoneNumberType'), (list) => {
            const defaultTypes = (list ?? [])
              .filter((phoneNumber) => phoneNumber.isDefault)
              .map((phoneNumber) => phoneNumber.type);

            return new Set(defaultTypes).size === defaultTypes.length;
          })
          .test('hasNoDuplicateNumbersPerType', function hasNoDuplicateNumbersPerType(list) {
            const seenKeys = new Set<string>();
            const duplicateErrors: yup.ValidationError[] = [];

            (list ?? []).forEach((phoneNumber, index) => {
              const fullNumber = removeWhitespaces(
                `${phoneNumber.countryCode ?? ''}${phoneNumber.number ?? ''}`,
              );

              if (!removeWhitespaces(phoneNumber.number ?? '')) return;

              const key = `${phoneNumber.type}|${fullNumber}`;

              if (seenKeys.has(key))
                duplicateErrors.push(
                  this.createError({
                    message: t('duplicatePhoneNumberInList'),
                    path: `${this.path}[${index}].number`,
                  }),
                );

              seenKeys.add(key);
            });

            return duplicateErrors.length ? new yup.ValidationError(duplicateErrors) : true;
          }),
      }),
    [multiPhoneNumberSchema, name, t],
  );

  const extend = useCallback(
    <TSchema extends yup.AnyObjectSchema>(toExtendSchema: TSchema) =>
      toExtendSchema.shape(schema.fields),
    [schema],
  );

  return useMemo(
    () => ({
      extend,
      schema,
    }),
    [extend, schema],
  );
};

export { usePhoneNumberLine, usePhoneNumberLines, usePhoneNumbersSchema };
